import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { ClarificationUploadedDocuments } from "@modules/update-documents/ClarificationUploadedDocuments";
import { useDispatch } from "react-redux";
import { postSeekClarificationResponseThunk } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import { AppDispatch, useAppSelector } from "@store/store";
import { openFileBySignedUrl } from "../../utils/getViewSignedUrlHandler";
import {
  getFileROWSignedURLThunk,
  getFileSignedURLThunk,
  getDocumentsListThunk,
  getUploadedFileByIdThunk,
} from "@modules/stepper/redux/thunk/documentThunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { toast } from "react-toastify";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import {
  clearClarificationData,
  clearAccessHoldPayload,
} from "@components/slider/redux/clarificationSlice";

const MAX_REMARKS = 2000;

const UpdateDocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const accessId = searchParams.get("access");
  const rowId = searchParams.get("row");
  const dispatch = useDispatch<AppDispatch>();

  const applicationDetails = useAppSelector((state) => state.applicationDetails?.applicationDetails,);

  const applicationDetailsROW = useAppSelector((state) => state.applicationDetailsROW?.applicationDetailsROW,);

  const clarificationData = useAppSelector((state: any) => state.clarification.clarificationData,);

  // ✅ NEW: stashed step2_details from AccessQuestionMain
  const accessHoldPayload = useAppSelector((state: any) => state.clarification.accessHoldPayload,);

  const documentsToUpdate = clarificationData?.documents_to_updated || [];
  const [fullFlowDocuments, setFullFlowDocuments] = useState<any[]>([]);

  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]); // remarks attachments
  const [remarksOpen, setRemarksOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const QuestionsUpdateCheck = clarificationData?.questions_to_updated;
  const documentRequestNullCheck = clarificationData?.documents_to_updated?.length > 0;

  const accessPayloadCheck = (QuestionsUpdateCheck && documentRequestNullCheck) // add access hold payload only this will be true until current flow will work 
  const isLocationChangedDocumentFlow =
    !!accessHoldPayload?.step2_details &&
    accessHoldPayload.isLocationTypeChanged === true;
  const shouldAttachStep2Details =
    accessPayloadCheck || isLocationChangedDocumentFlow;
  const categoryId = accessId ? 1 : 2;
  const effectiveDocuments = useMemo(
    () => (isLocationChangedDocumentFlow ? fullFlowDocuments : documentsToUpdate),
    [isLocationChangedDocumentFlow, fullFlowDocuments, documentsToUpdate],
  );

  useEffect(() => {
    const fetchFullFlowDocuments = async () => {
      if (!isLocationChangedDocumentFlow || !clarificationData?.application_id) {
        setFullFlowDocuments([]);
        return;
      }

      const documentListData = await dispatch(
        getDocumentsListThunk({
          category_id: categoryId,
          access_id: clarificationData.application_id,
          document: "file_upload",
          
        }),
      );

      const baseDocs = documentListData?.payload?.data || [];

      const uploadedFileById = await dispatch(
        getUploadedFileByIdThunk({
          access_id: clarificationData.application_id,
          category_id: categoryId,
          document: "file_upload",
        }),
      );

      const uploadedFilesList = uploadedFileById?.payload?.data?.files || [];

      const mergedDocs = baseDocs.map((doc: any) => {
        const uploadedFile =
          uploadedFilesList.length > 0 &&
          uploadedFilesList.find((file: any) => file.document_id === doc.document_id);

        return uploadedFile ? { ...doc, uploadedFile } : doc;
      });

      setFullFlowDocuments(mergedDocs);
    };

    fetchFullFlowDocuments();
  }, [
    isLocationChangedDocumentFlow,
    clarificationData?.application_id,
    categoryId,
    dispatch,
  ]);


  const handleSubmitRemarks = async () => {
    if (!clarificationData) return;

    const payload: Record<string, any> = {
      application_id: clarificationData.application_id,

      recipient_id: clarificationData.sender_details.sender_id,

      recipient_role: clarificationData.sender_role,

      documents_to_updated: uploadedFiles.map((file) => file.document_id),  // optional

      uploaded_documents: uploadedFiles.map((file) => ({ // optional
        document_id: file.document_id,
        originalFileName: file.file_name,
        fileName: file.document_name,
        objectKey: file.objectKey,
      })),
      message: remarks,
      clk_documents: attachments.map((file) => ({
        fileName: file.fileName,
        originalFileName: file.document_name,
        objectKey: file.objectKey,
        mimeType: "pdf",
      })),

      // ✅ merge stashed step2_details for both clarification-both-case and location-change flow
      ...(shouldAttachStep2Details && accessHoldPayload?.step2_details
        ? { step2_details: accessHoldPayload.step2_details }
        : {}),
    };

    console.log("Payload for submission:", payload);

    const response = await dispatch(
      postSeekClarificationResponseThunk(payload),
    );

    if (response.payload.success) {
      toast.success("Access Amenities Information saved successfully");
    } else {
      toast.error(
        response?.payload?.message || "Failed to save application",
      );
    }

    if (postSeekClarificationResponseThunk.fulfilled.match(response)) {
      setRemarksOpen(false);
      setSuccessOpen(true);
      setSuccessMessage(response.payload.message);
    }
  };

  const { allDocumentsUploaded, uploadPercentage } = useMemo(() => {
    if (!effectiveDocuments.length) {
      return { allDocumentsUploaded: false, uploadPercentage: 0 };
    }

    const requiredDocuments = effectiveDocuments.filter(
      (doc: any) => doc.upload_required !== false,
    );
    const uploadedRequiredDocuments = requiredDocuments.filter((doc: any) =>
      uploadedFiles.some(
          (uploaded: any) => uploaded.document_id === doc.document_id,
      ),
    );

    return {
      allDocumentsUploaded:
        uploadedRequiredDocuments.length === requiredDocuments.length,
      uploadPercentage: requiredDocuments.length
        ? Math.round(
            (uploadedRequiredDocuments.length / requiredDocuments.length) * 100,
          )
        : 100,
    };
  }, [effectiveDocuments, uploadedFiles]);

  const getTruncatedFileName = (fileName: string, maxLength = 25) => {
    const lastDot = fileName?.lastIndexOf(".");
    if (lastDot === -1) return fileName;

    const name = fileName?.slice(0, lastDot);
    const ext = fileName?.slice(lastDot);

    return (
      (name?.length > maxLength ? name?.slice(0, maxLength) + "..." : name) +
      ext
    );
  };

  const handleAttachmentUpload = async (
    file: File,
    fileInput: HTMLInputElement,
  ) => {
    try {
      if (file.size > 21 * 1024 * 1024) {
        toast.error("File size must be less than 20 MB");
        return;
      }

      const fileUploadURL = await dispatch(
        genericUploadThunk({
          file,
          moduleType: accessId ? "access" : "row",
        }),
      );

      if (!fileUploadURL?.payload?.success) {
        const fileUploadURLDetails =
          fileUploadURL?.payload?.response?.data?.details ||
          fileUploadURL?.payload?.details;

        toast.error(
          Array.isArray(fileUploadURLDetails)
            ? fileUploadURLDetails[0]
            : typeof fileUploadURLDetails === "string"
              ? fileUploadURLDetails
              : "Something Went Wrong!",
        );
        return;
      }

      if (!productionCheck) {
        const uploadSuccess = await dispatch(
          uploadToS3Thunk({
            signedUrl: fileUploadURL.payload.data.signedUrl,
            file,
            contentType: file.type,
          }),
        ).unwrap();

        if (!uploadSuccess) {
          throw new Error("File upload to S3 failed");
        }
      }

      const uploadedFile = {
        file_name: file.name, // original file name
        document_name: file.name, // uploaded file name
        fileName: fileUploadURL.payload.data.fileName,
        objectKey: fileUploadURL.payload.data.objectKey,
      };

      setAttachments((prev) => [...prev, uploadedFile]);

      toast.success("File uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload file");
    } finally {
      fileInput.value = "";
    }
  };

  const handleAttachmentChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const fileInput = event.target;
    const file = fileInput.files?.[0];

    if (!file) return;

    await handleAttachmentUpload(file, fileInput);
  };

  const handleViewFile = (doc_name: any) => {
    openFileBySignedUrl({
      document_name: doc_name,
      dispatch,
      getSignedUrlThunk: accessId
        ? getFileSignedURLThunk
        : getFileROWSignedURLThunk,
    });
  };

  const handleDeleteAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    setRemarks("");
    dispatch(clearClarificationData());
    dispatch(clearAccessHoldPayload());  // ✅ NEW: clear the stashed payload
    accessId
      ? navigate("/application-details", { replace: true })
      : navigate("/application-details-row", { replace: true });
  };

  const handleCancel = () => {
    dispatch(clearClarificationData());
    dispatch(clearAccessHoldPayload());  // ✅ NEW: clear the stashed payload
    navigate(-1);
  };

  if (!clarificationData) {
    return (
      <Navigate
        to={accessId ? "/application-details" : "/application-details-row"}
        replace
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#F3F2F7",
        px: { xs: 1.5, md: 3 },
        py: { xs: 1.5, md: 2 },
      }}
    >
      {/* Submit Remarks Dialog */}
      <Dialog
        open={remarksOpen}
        onClose={() => setRemarksOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            width: "100%",
            maxWidth: "480px",
            overflowX: "hidden",
            p: 0,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            pt: 3,
            pb: 1,
            fontSize: "18px",
            fontWeight: 700,
            color: "#1F1F1F",
          }}
        >
          Submit Your Remarks
          <IconButton onClick={() => setRemarksOpen(false)} size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1, overflowY: "auto", flex: 1, }}>
          <Box
            component="textarea"
            value={remarks}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setRemarks(e.target.value.slice(0, MAX_REMARKS))
            }
            placeholder="Enter Remarks"
            rows={5}
            sx={{
              width: "100%",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              p: 1.5,
              fontSize: "14px",
              fontFamily: "Inter, sans-serif",
              color: "#212121",
              resize: "none",
              outline: "none",
              boxSizing: "border-box",
              "&::placeholder": { color: "#9CA3AF" },
            }}
          />
          <Typography
            sx={{
              fontSize: "12px",
              color: "#9CA3AF",
              textAlign: "right",
              mt: 0.5,
            }}
          >
            {remarks.length}/{MAX_REMARKS}
          </Typography>
          <Box sx={{ mt: 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                mb: 1,
              }}
            >
              Upload Documents (optional)
            </Typography>

            <Button
              component="label"
              variant="outlined"
              sx={{
                mb: 2,
                textTransform: "none",
              }}
            >
              Upload Document
              <input
                hidden
                type="file"
                accept=".pdf"
                multiple
                onChange={handleAttachmentChange}
                max={5}
              />
            </Button>

            {attachments?.length > 0 && (
              <Box sx={{ maxWidth: "100%" }}>
                {attachments.map((attachment: any, index: number) => (
                  <Box
                    key={`${attachment.file_name}-${index}`}
                    sx={{
                      border: "1px solid #9FA3A9",
                      borderRadius: "8px",
                      px: 1.8,
                      py: 1.15,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                      backgroundColor: "#F6F6F7",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <PictureAsPdfIcon
                        sx={{
                          color: "#D2413A",
                        }}
                      />

                      <Typography>
                        {getTruncatedFileName(attachment.file_name)}
                      </Typography>
                    </Box>

                    <Box>
                      <IconButton
                        onClick={() => handleViewFile(attachment.fileName)}
                      >
                        <CloudDownloadOutlinedIcon />
                      </IconButton>

                      <IconButton onClick={() => handleDeleteAttachment(index)}>
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            px: 3,
            py: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setRemarksOpen(false);
              setRemarks("");
            }}
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              borderColor: "#D1D5DB",
              color: "#374151",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={remarks.trim().length === 0}
            onClick={handleSubmitRemarks}
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              backgroundColor: "#3A1C73",
              px: 3,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#2d1559" },
              "&.Mui-disabled": {
                backgroundColor: "#E5E7EB",
                color: "#9CA3AF",
              },
            }}
          >
            Submit
          </Button>
        </Box>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successOpen}
        onClose={handleSuccessClose}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            width: "100%",
            maxWidth: "480px",
            p: 0,
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: 3,
            py: 3,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 80, color: "#22C55E", mb: 2 }} />
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#1F1F1F",
              textAlign: "center",
              mb: 2,
            }}
          >
            {successMessage}
          </Typography>
          <Button
            variant="contained"
            onClick={handleSuccessClose}
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              backgroundColor: "#3A1C73",
              px: 3,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#2d1559" },
            }}
          >
            Ok, Got it.
          </Button>
        </DialogContent>
      </Dialog>

      <Box sx={{ maxWidth: "1800px", mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "#9C8FB8",
            mb: 3,
          }}
        >
          <HomeRoundedIcon sx={{ fontSize: 18 }} />
          <ChevronRightRoundedIcon sx={{ fontSize: 12 }} />
          <Typography
            sx={{ fontSize: "12px", color: "#757575", lineHeight: "18px" }}
          >
            {accessId
              ? applicationDetails?.application_code
              : applicationDetailsROW?.application_code}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#212121",
            mb: 1.5,
            lineHeight: "42px",
          }}
        >
          Update Drawing and Document
        </Typography>

        <Card
          sx={{
            borderRadius: "16px",
            border: "1px solid #E2E5EC",
            boxShadow: "none",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box sx={{ px: { xs: 1.5, md: 2 }, py: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
              }}
            >
              <Typography
                sx={{ fontSize: "18px", fontWeight: 700, color: "#212121" }}
              >
                Upload Documents
              </Typography>
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={40}
                  thickness={4}
                  sx={{ color: "#CFDAE7" }}
                  aria-hidden="true"
                />
                <CircularProgress
                  variant="determinate"
                  value={uploadPercentage}
                  size={40}
                  thickness={4}
                  aria-label="Required document upload completion"
                  sx={{ color: "#2E844A", position: "absolute", left: 0 }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    {uploadPercentage}%
                  </Typography>
                </Box>
              </Box>
            </Box>

            <ClarificationUploadedDocuments
              documents={effectiveDocuments}
              applicationId={clarificationData?.application_id}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
            />
          </Box>

          <Divider />

          <Box
            sx={{
              px: { xs: 1.5, md: 2 },
              py: 1.5,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                borderColor: "#D1D5DB",
                color: "#374151",
                px: 2.5,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!allDocumentsUploaded}
              onClick={() => setRemarksOpen(true)}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                backgroundColor: "#3A1C73",
                px: 2.5,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#2d1559" },
              }}
            >
              Next
            </Button>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default UpdateDocumentsPage;